import React, { useState, useMemo, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { useVehicles } from '../../../context/VehicleContext'
import CyberButton from '../SAMenu/CyberButton'
import './DetailsPage.css'

const TICKET_API_URL = `${process.env.REACT_APP_HOST_API_URL}/vehicle/tracking/mg-vehicle-master`
const PAGE_SIZE = 50

// ── shared dark-theme style helpers ──────────────────────────────────────────

const thBase = {
  fontWeight: 700, padding: '10px 14px', fontSize: 12,
  whiteSpace: 'nowrap', border: '1px solid #424666', textAlign: 'left',
}
const th = (bg = '#252a4a', color = 'rgba(255,255,255,0.7)') => ({ ...thBase, background: bg, color })

const tdBase = {
  padding: '9px 14px', fontSize: 13, border: '1px solid #2e3358', color: '#ffffff',
}
const td = (extra = {}) => ({ ...tdBase, ...extra })

const darkSelect = {
  background: '#1a1e35', border: '1px solid #424666', color: '#fff',
  borderRadius: 6, padding: '6px 10px', fontSize: 13, cursor: 'pointer', outline: 'none',
}
const darkInput = {
  background: '#1a1e35', border: '1px solid #424666', color: '#fff',
  borderRadius: 6, padding: '6px 30px 6px 10px', fontSize: 13, outline: 'none',
  width: '100%', boxSizing: 'border-box',
}

function fmtDate(val) {
  if (!val || val === '0001-01-01 00:00:00') return '-'
  try {
    return new Date(val).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata', hour12: false,
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return val }
}

function Pill({ value, color, bg }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color, background: bg,
      borderRadius: 20, padding: '2px 10px', whiteSpace: 'nowrap',
    }}>
      {value || '-'}
    </span>
  )
}

function SimplePagination({ page, totalPages, total, pageSize, onChange }) {
  if (totalPages <= 1) return null
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
      <CyberButton onClick={() => onChange(1)} style={{ opacity: page === 1 ? 0.4 : 1, pointerEvents: page === 1 ? 'none' : 'auto' }}>«</CyberButton>
      <CyberButton onClick={() => onChange(page - 1)} style={{ opacity: page === 1 ? 0.4 : 1, pointerEvents: page === 1 ? 'none' : 'auto' }}>‹</CyberButton>
      <span style={{ margin: '0 8px' }}>{start}–{end} of {total}</span>
      <CyberButton onClick={() => onChange(page + 1)} style={{ opacity: page === totalPages ? 0.4 : 1, pointerEvents: page === totalPages ? 'none' : 'auto' }}>›</CyberButton>
      <CyberButton onClick={() => onChange(totalPages)} style={{ opacity: page === totalPages ? 0.4 : 1, pointerEvents: page === totalPages ? 'none' : 'auto' }}>»</CyberButton>
    </div>
  )
}

const SUP_STATUS_COLORS = {
  CLOSED: { color: '#6ee7b7', bg: 'rgba(5,150,105,0.25)' },
  OPEN: { color: '#fca5a5', bg: 'rgba(220,38,38,0.25)' },
  IN_PROGRESS: { color: '#fdba74', bg: 'rgba(249,115,22,0.25)' },
  REJECTED: { color: '#f87171', bg: 'rgba(220,38,38,0.20)' },
  COMPLETED: { color: '#6ee7b7', bg: 'rgba(5,150,105,0.20)' },
}

const DRIVE_STATUS_COLORS = {
  Deployable: { color: '#6ee7b7', bg: 'rgba(5,150,105,0.20)' },
  NonDeployable: { color: '#fca5a5', bg: 'rgba(220,38,38,0.20)' },
  Deployed: { color: '#ffffff', bg: 'rgba(37,99,235,0.30)' },
}

const SUP_STATUS_OPTIONS = ['All', 'OPEN', 'IN_PROGRESS', 'CLOSED', 'REJECTED', 'COMPLETED']

const TECH_STATUS_COLORS = {
  IN_PROGRESS: { color: '#fdba74', bg: 'rgba(249,115,22,0.25)' },
  COMPLETED: { color: '#6ee7b7', bg: 'rgba(5,150,105,0.25)' },
  PENDING: { color: '#fca5a5', bg: 'rgba(220,38,38,0.25)' },
}

function TicketDetailsTab({ vehicles }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastFetched, setLastFetched] = useState(null)
  const [search, setSearch] = useState('')
  const [supFilter, setSupFilter] = useState('All')
  const [driveFilter, setDriveFilter] = useState('All')
  const [cityFilter, setCityFilter] = useState('All cities')
  const [hubFilter, setHubFilter] = useState('All hubs')
  const [page, setPage] = useState(1)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch(TICKET_API_URL)
      const json = await res.json()
      setRows(json?.data || [])
      setLastFetched(new Date())
    } catch (e) {
      setError('Failed to load ticket data: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleCityChange = (city) => {
    setCityFilter(city)
    setHubFilter('All hubs')
    setPage(1)
  }

  const windowedRows = useMemo(() => {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - 30)
    return rows.filter(r => {
      const dateVal = r.sup_status_on || r.tech_status_date
      return dateVal && dateVal !== '0001-01-01 00:00:00' && new Date(dateVal) >= cutoff
    })
  }, [rows])

  const cityOptions = useMemo(() => {
    const cities = Array.from(new Set(vehicles.map(v => v.mg?.city).filter(Boolean))).sort()
    return ['All cities', ...cities]
  }, [vehicles])

  const hubOptions = useMemo(() => {
    const source = cityFilter !== 'All cities'
      ? vehicles.filter(v => v.mg?.city === cityFilter)
      : vehicles
    const hubs = Array.from(new Set(source.map(v => v.mg?.depot_name).filter(Boolean))).sort()
    return ['All hubs', ...hubs]
  }, [vehicles, cityFilter])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return windowedRows
      .filter(r => {
        const matchSup = supFilter === 'All' || (supFilter === '-' ? !r.sup_status || r.sup_status === '-' : r.sup_status === supFilter)
        const matchDrive = driveFilter === 'All' || r.drive_status === driveFilter
        const matchCity = cityFilter === 'All cities' || r.city === cityFilter
        const matchHub = hubFilter === 'All hubs' || r.depot_name === hubFilter
        const matchSearch = !q ||
          (r.VehicleNumber || r.vehicle_no || '').toLowerCase().includes(q) ||
          (r.TicketId || '').toLowerCase().includes(q) ||
          (r.Typeofincident || '').toLowerCase().includes(q)
        return matchSup && matchDrive && matchCity && matchHub && matchSearch
      })
      .sort((a, b) => new Date(b.sup_status_on || b.tech_status_date || 0) - new Date(a.sup_status_on || a.tech_status_date || 0))
  }, [windowedRows, driveFilter, supFilter, cityFilter, hubFilter, search])

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const exportTickets = () => {
    const data = filtered.map(r => ({
      'Vehicle No': r.VehicleNumber || r.vehicle_no || '-',
      'Drive Status': r.drive_status || '-',
      'Ticket ID': r.TicketId || '-',
      'Type of Incident': r.Typeofincident || '-',
      'Supervisor Status': r.sup_status || '-',
      'Sup Status On': fmtDate(r.sup_status_on),
      'Tech Status': r.tech_status || '-',
      'Tech Status Date': fmtDate(r.tech_status_date),
      'Assigned To Tech': fmtDate(r.assinged_to_technitian),
      'Is Active': r.is_active || '-',
    }))
    const ws = XLSX.utils.json_to_sheet(data)
    ws['!cols'] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 4, 16) }))
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Ticket Details')
    XLSX.writeFile(wb, `ticket-details-${new Date().toISOString().slice(0, 10)}.xlsx`)
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#fff', letterSpacing: 0.2 }}>Ticket Details</h2>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
            Live from Vehicle Master API · last 30 days
            {lastFetched && <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.3)' }}>· Fetched: {lastFetched.toLocaleTimeString('en-IN')}</span>}
          </div>
        </div>
        <CyberButton onClick={load}>{loading ? 'Loading…' : '↻ Refresh'}</CyberButton>
        <CyberButton onClick={exportTickets}>Export Excel</CyberButton>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <input
            type="text"
            placeholder="Search vehicle no., ticket ID, incident…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            style={darkInput}
          />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1) }} style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)',
            }}>✕</button>
          )}
        </div>
        <select value={supFilter} onChange={e => { setSupFilter(e.target.value); setPage(1) }} style={darkSelect}>
          {SUP_STATUS_OPTIONS.map(opt => (
            <option key={opt} value={opt} style={{ background: '#1a1e35' }}>{opt === 'All' ? 'All Sup. Status' : opt}</option>
          ))}
        </select>
        <select value={driveFilter} onChange={e => { setDriveFilter(e.target.value); setPage(1) }} style={darkSelect}>
          {['All', 'Deployable', 'NonDeployable'].map(opt => (
            <option key={opt} value={opt} style={{ background: '#1a1e35' }}>{opt === 'All' ? 'All Drive Status' : opt}</option>
          ))}
        </select>
        <select value={cityFilter} onChange={e => handleCityChange(e.target.value)} style={darkSelect}>
          {cityOptions.map(c => (
            <option key={c} value={c} style={{ background: '#1a1e35' }}>{c}</option>
          ))}
        </select>
        <select value={hubFilter} onChange={e => { setHubFilter(e.target.value); setPage(1) }} style={darkSelect}>
          {hubOptions.map(h => (
            <option key={h} value={h} style={{ background: '#1a1e35' }}>{h}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginLeft: 'auto' }}>
          {filtered.length} of {windowedRows.length} records
        </span>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>Loading ticket data…</div>
      )}
      {error && (
        <div style={{ padding: '12px 16px', background: 'rgba(220,38,38,0.2)', border: '1px solid rgba(252,165,165,0.3)', borderRadius: 8, color: '#fca5a5', marginBottom: 14 }}>{error}</div>
      )}

      {!loading && !error && (
        <>
          <SimplePagination page={page} totalPages={totalPages} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />
          <div style={{
            overflowX: 'auto', borderRadius: 8, border: '1px solid #424666',
            background: 'linear-gradient(180deg, #252a4a 0%, #1e2240 100%)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          }}>
            <table className="details-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#252a4a' }}>
                  <th style={th()}>#</th>
                  <th style={th('#5241a0', '#e9d5ff')}>Vehicle No.</th>
                  <th style={th()}>Drive Status</th>
                  <th style={th('#1a3a60', '#ffffff')}>Ticket ID</th>
                  <th style={th()}>Type of Incident</th>
                  <th style={th('#612525', '#fca5a5')}>Supervisor Status</th>
                  <th style={th()}>Sup. Status On</th>
                  <th style={th('#1a6347', '#6ee7b7')}>Tech Status</th>
                  <th style={th()}>Tech Status Date</th>
                  <th style={th()}>Assigned to Tech</th>
                  <th style={th()}>Is Active</th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr><td colSpan={11} style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No records found</td></tr>
                ) : paginated.map((r, i) => {
                  const driveColors = DRIVE_STATUS_COLORS[r.drive_status] || { color: '#c4b5fd', bg: 'rgba(100,44,169,0.2)' }
                  const supColors = SUP_STATUS_COLORS[r.sup_status] || { color: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.08)' }
                  const techColors = TECH_STATUS_COLORS[r.tech_status] || { color: 'rgba(255,255,255,0.6)', bg: 'rgba(255,255,255,0.08)' }
                  return (
                    <tr key={r.TicketId || i} style={{ background: i % 2 === 0 ? '#1e2240' : '#252a4a' }}>
                      <td style={td({ color: '#fff', fontSize: 11 })}>{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td style={td({ fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' })}>{r.VehicleNumber || r.vehicle_no || '-'}</td>
                      <td style={td({ color: '#fff' })}><Pill value={r.drive_status} color={driveColors.color} bg={driveColors.bg} /></td>
                      <td style={td({ color: '#fff', fontWeight: 600 })}>{r.TicketId || '-'}</td>
                      <td style={td({ color: '#fff' })}>{r.Typeofincident || '-'}</td>
                      <td style={td({ color: '#fff' })}><Pill value={r.sup_status} color={supColors.color} bg={supColors.bg} /></td>
                      <td style={td({ whiteSpace: 'nowrap', fontSize: 12, color: '#fff' })}>{fmtDate(r.sup_status_on)}</td>
                      <td style={td({ color: '#fff' })}><Pill value={r.tech_status} color={techColors.color} bg={techColors.bg} /></td>
                      <td style={td({ whiteSpace: 'nowrap', fontSize: 12, color: '#fff' })}>{fmtDate(r.tech_status_date)}</td>
                      <td style={td({ whiteSpace: 'nowrap', fontSize: 12, color: '#fff' })}>{fmtDate(r.assinged_to_technitian)}</td>
                      <td style={td({ textAlign: 'center', color: '#fff' })}>
                        <Pill
                          value={r.is_active === 'Y' ? 'Active' : r.is_active === 'N' ? 'Inactive' : (r.is_active || '-')}
                          color={r.is_active === 'Y' ? '#6ee7b7' : '#fca5a5'}
                          bg={r.is_active === 'Y' ? 'rgba(5,150,105,0.2)' : 'rgba(220,38,38,0.2)'}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ── Main DetailsPage ──────────────────────────────────────────────────────────

export default function DetailsPage() {
  const { vehicles, loading, error } = useVehicles()

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'rgba(255,255,255,0.5)', fontSize: 15 }}>
        Loading…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: '#fca5a5', fontSize: 14 }}>{error}</div>
    )
  }

  return (
    <div style={{ padding: '20px 24px', color: '#fff', minHeight: '100%' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: 0.3 }}>Details</h1>
      </div>
      <TicketDetailsTab vehicles={vehicles} />
    </div>
  )
}
